import { createTheme } from '@mui/material/styles';

// Bellow color palette — clean modern with burnt orange accent
const bellowColors = {
  // Clean neutral backgrounds
  cream: '#1a1a2e',       // App background — dark navy
  creamDark: '#151525',   // Darker variant
  creamLight: '#22223c',  // Slightly lighter

  // Text — near-black for maximum readability
  bellowBlack: '#1A1A2E', // Deep ink (was #2C1810)
  bellowDark: '#0F0F1A',  // Darker variant (was #1A0E08)

  // Supporting neutrals
  warmWhite: '#fffeed',   // Cream for cards/paper
  cardBg: '#fffeed',      // Cream cards on dark background
  cardBgHover: '#f5f4e0', // Subtle hover variant

  // Accent colors — red-orange, the flame
  accent: '#ff371d',      // Red-orange accent (updated)
  accentLight: '#ff5c45', // Lighter variant
  accentTint: 'rgba(209, 75, 31, 0.08)', // Subtle orange tint for backgrounds

  // Headings — use dark text, not orange (reserve orange for actions)
  headingRed: '#1A1A2E',  // Dark ink for headings (was #CC3D15)

  // CTA / button color — red-orange, primary interactive color
  ctaOrange: '#ff371d',   // Updated to match new design
  ctaDark: '#cc2a12',     // Darker variant

  // Semantic colors
  success: '#2D9F4F',     // Cleaner green (was #4D8B31)
  warning: '#E5A40A',     // Slightly brighter (was #D4960A)
  error: '#DC3545',       // Standard modern red (was #C0392B)

  // Grays — neutral, clean
  gray100: '#F5F5F5',     // Lightest gray (was #F7F0E5)
  gray200: '#E5E5E5',     // Light gray (was #E8DFD0)
  gray300: '#CCCCCC',     // Mid-light gray (was #D1C4AE)
  gray400: '#888888',     // Mid gray (was #7A6A54)
  gray500: '#555555',     // Dark gray (was #5C4A36)
};

// M3 Motion tokens
const m3Motion = {
  easing: {
    standard: 'cubic-bezier(0.2, 0, 0, 1)',
    emphasized: 'cubic-bezier(0.2, 0, 0, 1)',
    decelerate: 'cubic-bezier(0, 0, 0, 1)',
    accelerate: 'cubic-bezier(0.3, 0, 1, 1)',
  },
  duration: {
    short1: 50,
    short2: 100,
    short3: 150,
    short4: 200,
    medium1: 250,
    medium2: 300,
    medium3: 350,
    medium4: 400,
    long1: 450,
    long2: 500,
  },
};

const theme = createTheme({
  palette: {
    primary: {
      main: bellowColors.ctaOrange,
      light: bellowColors.accentLight,
      dark: bellowColors.ctaDark,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: bellowColors.cream,
      light: bellowColors.creamLight,
      dark: bellowColors.creamDark,
      contrastText: bellowColors.bellowBlack,
    },
    background: {
      default: '#1a1a2e',
      paper: '#fffeed',
    },
    text: {
      primary: bellowColors.bellowBlack,
      secondary: bellowColors.gray500,
    },
    success: {
      main: bellowColors.success,
    },
    warning: {
      main: bellowColors.warning,
    },
    error: {
      main: bellowColors.error,
    },
    // Custom colors accessible via theme.palette.bellow
    bellow: bellowColors,
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: 'clamp(2rem, 5vw, 2.5rem)',
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
      color: bellowColors.headingRed,
    },
    h2: {
      fontWeight: 700,
      fontSize: 'clamp(1.75rem, 4vw, 2rem)',
      lineHeight: 1.25,
      color: bellowColors.headingRed,
    },
    h3: {
      fontWeight: 600,
      fontSize: 'clamp(1.5rem, 3vw, 1.75rem)',
      lineHeight: 1.3,
      color: bellowColors.headingRed,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.35,
      color: bellowColors.bellowBlack,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.4,
      color: bellowColors.bellowBlack,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.4,
      color: bellowColors.bellowBlack,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      fontWeight: 400,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      fontWeight: 400,
    },
    button: {
      fontWeight: 600,
      fontSize: '1rem',
      textTransform: 'none',
      letterSpacing: '0.02em',
    },
  },
  shape: {
    borderRadius: 16,
  },
  // M3 transitions using standard easing
  transitions: {
    easing: {
      easeInOut: m3Motion.easing.standard,
      easeOut: m3Motion.easing.decelerate,
      easeIn: m3Motion.easing.accelerate,
      sharp: m3Motion.easing.emphasized,
    },
    duration: {
      shortest: m3Motion.duration.short2,
      shorter: m3Motion.duration.short4,
      short: m3Motion.duration.medium1,
      standard: m3Motion.duration.medium2,
      complex: m3Motion.duration.medium4,
      enteringScreen: m3Motion.duration.medium1,
      leavingScreen: m3Motion.duration.short4,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          minHeight: 48,
          minWidth: 48,
          borderRadius: 24,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '1rem',
          letterSpacing: '0.02em',
        },
        containedPrimary: {
          backgroundColor: bellowColors.ctaOrange,
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: bellowColors.ctaDark,
          },
        },
        containedSecondary: {
          backgroundColor: bellowColors.gray100,
          color: bellowColors.bellowBlack,
          '&:hover': {
            backgroundColor: bellowColors.gray200,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          minHeight: 48,
          minWidth: 48,
        },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          height: 64,
          backgroundColor: bellowColors.warmWhite,
          borderTop: `1px solid ${bellowColors.gray200}`,
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          minHeight: 64,
          minWidth: 48,
          color: bellowColors.gray400,
          '&.Mui-selected': {
            color: bellowColors.bellowBlack,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          height: 36,
          borderRadius: 12,
          fontWeight: 500,
        },
        filled: {
          backgroundColor: bellowColors.gray100,
          color: bellowColors.bellowBlack,
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 12px rgba(209, 75, 31, 0.25)',
          '&:hover': {
            boxShadow: '0 6px 16px rgba(209, 75, 31, 0.35)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
          backgroundColor: bellowColors.cardBg,
          transition: `box-shadow ${m3Motion.duration.short4}ms ${m3Motion.easing.standard}, transform ${m3Motion.duration.short4}ms ${m3Motion.easing.standard}`,
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)',
            transform: 'translateY(-1px)',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 48,
        },
        indicator: {
          backgroundColor: bellowColors.bellowBlack,
          height: 3,
          borderRadius: 3,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 48,
          minWidth: 48,
          textTransform: 'none',
          fontWeight: 500,
          color: bellowColors.gray400,
          '&.Mui-selected': {
            color: bellowColors.bellowBlack,
            fontWeight: 600,
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          minHeight: 48,
          borderRadius: 12,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            backgroundColor: bellowColors.warmWhite,
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          color: bellowColors.bellowBlack,
          boxShadow: 'none',
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: bellowColors.ctaOrange,
          color: '#FFFFFF',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 24,
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(26, 26, 46, 0.2)',
        },
      },
    },
  },
});

export default theme;
